// @flow
import React, { type Element } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { compose, pure } from 'recompose';
import { Alert, Button, Col, Row } from 'angieslist-react-components';
import { analytics, Track, urlCatalog } from 'angieslist-app-platform';
import Loader from 'angieslist-app-platform/lib/browser/Loader';
import { submit } from 'redux-form';
import { get, isEmpty } from 'lodash';
import moment from 'moment';
import { Link } from 'react-router';
import EditContactInformation from './EditContactInformation';
import EditHours from './EditHours';
// eslint-disable-next-line import/no-named-as-default
import ServiceDetails from './ServiceDetails';
import EditLicenseInformation from './EditLicenseInformation';
import profileActions from '../../common/profileActions';
import { itemClicked } from '../../../../app/browser/analyticsHelpers';
import ShowcaseIndex from './ShowcaseIndex';
import type { ProfileReducerState } from '../../common/profileReducer';
import About from './About';

export type Props = {
  activeMenuItem: string,
  section: string,
  serviceProviderId: number,
  userId: string,
  serviceProviderCache: any,
  form: any,
  mobileUser: boolean,
  dispatch: (submission: any) => void,
  getShowcases: typeof profileActions.getShowcases,
  serviceProviderProfile: ProfileReducerState,
  saveAbout: typeof profileActions.saveAbout,
  saveBusinessHours: typeof profileActions.saveBusinessHours,
  saveContactInformation: typeof profileActions.saveContactInformation,
  saveServices: typeof profileActions.saveServices,
  isAdvertiser: boolean,
  isNorthStar: boolean,
  isTestUser: boolean,
  profileUrl: string,
};

const formDetails = {
  about: {
    pageName: 'About',
    formName: 'aboutForm',
  },
  'business-details': {
    pageName: 'Business Details',
    formName: 'businessDetailsForm',
  },
  hours: {
    pageName: 'Business Hours',
    formName: 'editHoursForm',
  },
  'contact-information': {
    pageName: 'Contact Information',
    formName: 'contactInformationForm',
  },
  'service-details': {
    pageName: 'Service details',
    formName: 'serviceDetailsForm',
  },
  licensing: {
    pageName: 'Licensing',
    formName: 'editLicenseForm',
  },
};

export const ContentComponent = ({
  activeMenuItem,
  serviceProviderId,
  userId,
  profileUrl,
  dispatch,
  form,
  serviceProviderProfile: {
    currentProfileUAPI,
    saveProfile: {
      isLoading: saveProfileIsLoading,
      error: saveProfileError,
    },
  },
  getShowcases,
  saveAbout,
  saveBusinessHours,
  saveServices,
  saveContactInformation,
  mobileUser,
  isAdvertiser,
  isNorthStar,
  isTestUser,
  serviceProviderCache,
}: Props): Element<"div"> => {
  const { pageName, formName } = get(formDetails, activeMenuItem, 'businessDetailsForm');

  const formSuccess = get(form, `${formName}.submitSucceeded`, false);

  // Service details
  const servicesOffered = get(currentProfileUAPI, 'response.servicesOffered');
  const servicesNotOffered = get(currentProfileUAPI, 'response.servicesNotOffered');

  // Hours
  const hoursCollection = get(currentProfileUAPI, 'response.businessInfo.hoursInfo.hoursInfoDetail');
  const days = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const timeFormats = ['hh:mm a', 'h:mm a', 'HH:mm', 'H:mm'];

  function formatHour(mins) {
    const time = mins || 0;
    return moment('00:00', timeFormats).add(time, 'minutes').format('hh:mm a');
  }

  let initialHours = {
    openMonday: '',
    closeMonday: '',
    isClosedMonday: true,
    openTuesday: '',
    closeTuesday: '',
    isClosedTuesday: true,
    openWednesday: '',
    closeWednesday: '',
    isClosedWednesday: true,
    openThursday: '',
    closeThursday: '',
    isClosedThursday: true,
    openFriday: '',
    closeFriday: '',
    isClosedFriday: true,
    openSaturday: '',
    closeSaturday: '',
    isClosedSaturday: true,
    openSunday: '',
    closeSunday: '',
    isClosedSunday: true,
    open247: false,
    byAppointment: false,
  };

  function isOpen247() {
    return hoursCollection.map(dayHours =>
      dayHours.startTime === 0 &&
      dayHours.endTime % 1440 === 0 &&
      !dayHours.isClosed).reduce((acc, cur) => acc && cur);
  }

  if (hoursCollection && hoursCollection.length) {
    initialHours.open247 = isOpen247();
    initialHours = Object.assign(hoursCollection.map((dayHours) => {
      const day = days[dayHours.dayOfWeek];
      return {
        [`open${day}`]: formatHour(dayHours.startTime),
        [`close${day}`]: isOpen247() ? formatHour(0) : formatHour(dayHours.endTime),
        [`isClosed${day}`]: dayHours.isClosed,
      };
    }).reduce((acc, current) => Object.assign(acc, current), {}), {
      open247: isOpen247(),
      byAppointment: get(currentProfileUAPI, 'response.businessInfo.hoursInfo.acceptsAppointments', false),
    });
  }

  const validPaymentTypes = {
    'Financing Available': 'financingAvailable',
    CreditCard: 'creditCard',
    Venmo: 'venmo',
    Zelle: 'zelle',
    Check: 'check',
    PayPal: 'payPal',
  };

  const initialPaymentTypes = get(currentProfileUAPI, 'response.businessInfo.paymentOptions', [])
    .reduce((acc, paymentType) => ({ ...acc, [`${validPaymentTypes[paymentType]}`]: true }), {});

  return (
    <div className="Content-Component">
      <Loader
        loading={isEmpty(currentProfileUAPI.response) || currentProfileUAPI.isLoading}
        element={
          <div className="text-center" style={{ marginTop: '1em' }}>
            <i className="icon icon-load icon-spin icon-3x text-primary" />
          </div>}
      >
        <div className="page-name-wrapper">
          <Link
            to={`/app/profile/${serviceProviderId}/`}
            id="back-button"
            className="visible-xs-inline"
          >
            <i className="icon icon-left-open" />&nbsp;Back
          </Link>
        </div>
        <If condition={formSuccess}>
          <Alert bsStyle="success" className="text-center">
            {pageName} updated successfully &mdash;&nbsp;
            <a
              id="edit-profile--success-view-my-profile"
              target="_blank"
              href={profileUrl || urlCatalog.member.serviceProvider(serviceProviderId)}
            >
              view your business profile
            </a>.
          </Alert>
        </If>
        <If condition={currentProfileUAPI.error || saveProfileError}>
          <Alert bsStyle="danger" className="text-center">
            There was an error updating your business.  Let us help you by contacting <a id="associate-management--error-phone-link" href={urlCatalog.global.businessCenterPhone()}>{urlCatalog.global.businessCenterPhoneText()}</a>.
          </Alert>
        </If>
        <Choose>
          <When condition={(isEmpty(activeMenuItem) && (!mobileUser)) || activeMenuItem === 'about'}>
            <About
              onSubmit={values =>
                 saveAbout(
                   serviceProviderId,
                   values.uuid,
                   values.inBusinessSince,
                   values.description,
                   values.businessHighlights,
                 )
              }
              initialValues={{
                businessName: get(currentProfileUAPI, 'response.businessInfo.businessName'),
                inBusinessSince: get(currentProfileUAPI, 'response.businessInfo.inBusinessSince'),
                description: get(currentProfileUAPI, 'response.businessInfo.businessDescription'),
                businessHighlights: get(currentProfileUAPI, 'response.businessInfo.businessHighlights'),
                uuid: get(currentProfileUAPI, 'response.uuid'),
              }}
              serviceProviderId={serviceProviderId}
            />
          </When>
          <When condition={activeMenuItem === 'contact-information'}>
            <EditContactInformation
              serviceProviderId={serviceProviderId}
              onSubmit={values => saveContactInformation(serviceProviderId, get(currentProfileUAPI, 'response.uuid'), values)}
            />
          </When>
          <When condition={activeMenuItem === 'hours'}>
            <EditHours
              onSubmit={values => saveBusinessHours(serviceProviderId, get(currentProfileUAPI, 'response.uuid'), values)}
              serviceProviderId={serviceProviderId}
              initialValues={initialHours}
            />
          </When>
          <When condition={activeMenuItem === 'service-details'}>
            <ServiceDetails
              onSubmit={values => saveServices(serviceProviderId, get(currentProfileUAPI, 'response.uuid'), values)}
              initialValues={{
                servicesOffered,
                servicesNotOffered,
                freeEstimates: get(currentProfileUAPI, 'response.businessInfo.amenities.freeEstimates') ? 'true' : 'false',
                warranty: get(currentProfileUAPI, 'response.businessInfo.amenities.warrantiesOffered') ? 'true' : 'false',
                emergencyService: get(currentProfileUAPI, 'response.businessInfo.amenities.emergencyServices'),
                ...initialPaymentTypes,
                LEEDAccredited: get(currentProfileUAPI, 'response.ecoFriendlyAccreditations.leedAccredited'),
                energyStarPartner: get(currentProfileUAPI, 'response.ecoFriendlyAccreditations.energyStarPartner'),
                EPALeadSafeCertified: get(currentProfileUAPI, 'response.ecoFriendlyAccreditations.epaLeadSafeCertified'),
                greenProductWorkPracticeProfessional: get(currentProfileUAPI, 'response.ecoFriendlyAccreditations.greenProductWorkPracticeProfessional'),
                seniorDiscount: get(currentProfileUAPI, 'response.businessInfo.amenities.seniorDiscount') ? 'true' : 'false',
                seniorDiscountAmount: get(currentProfileUAPI, 'response.businessInfo.amenities.seniorDiscount') ?
                                          get(currentProfileUAPI, 'response.businessInfo.amenities.seniorDiscountValue') : '',
                'COVID-19-VideoCalling': get(currentProfileUAPI, 'response.healthAndSafety.videoCalling'),
            }}
            />
          </When>
          <When condition={activeMenuItem === 'licensing'}>
            <EditLicenseInformation />
          </When>
          <When condition={activeMenuItem === 'showcase'}>
            <ShowcaseIndex
              {...{
                serviceProviderId,
                userId,
                getShowcases,
                isAdvertiser,
                isNorthStar,
                isTestUser,
              }}
            />
          </When>
          <Otherwise>
            <Alert bsStyle="danger">There was an error rendering this page.  Please use your browser&apos;s Back button to return to the previous page.</Alert>
          </Otherwise>
        </Choose>
        <If condition={!['showcase', 'business-highlights', 'licensing'].includes(activeMenuItem)}>
          <Row>
            <Col smPush={7} sm={5} mdPush={8} md={4} lgPush={9} lg={3}>
              <Track>
                <Button
                  id="edit-profile--submit-button"
                  bsStyle="primary-office"
                  className="pull-right"
                  onClick={() => dispatch(submit(formName))}
                  disabled={saveProfileIsLoading}
                  loading={saveProfileIsLoading}
                  data-track-location={`SP : My Business : ${pageName}`}
                  flat
                  block
                >
                  Save
                </Button>
              </Track>
            </Col>
          </Row>
        </If>
      </Loader>
    </div>
  );
};

const analyticsRuleset = {
  elements: {
    'edit-profile--submit-button': itemClicked('<<attributes.data-track-location.value>>', 'My Business : Save'),
  },
};

export default (compose(
  connect(state => ({
    form: state.form,
    serviceProviderProfile: state.serviceProviderProfile,
    serviceProviderCache: state.serviceProviderCache,
  }), dispatch => ({
    dispatch,
    ...bindActionCreators(profileActions, dispatch),
  })),
  analytics({ ruleset: analyticsRuleset }),
  pure,
)(ContentComponent): any);



// WEBPACK FOOTER //
// ./src/modules/profile/browser/components/Content.js